import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import ScrollToTop from "@/components/ScrollToTop";
import AnalyticsTracker from "@/components/AnalyticsTracker";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import { usePushNotifications } from "@/hooks/usePushNotifications";

// Pages
import HomePage from "@/pages/HomePage";
import CargoBookingPage from "@/pages/CargoBookingPage";
import CourierBookingPage from "@/pages/CourierBookingPage";
import TowingBookingPage from "@/pages/TowingBookingPage";
import MoversBookingPage from "@/pages/MoversBookingPage";
import RoadsideAssistancePage from "@/pages/RoadsideAssistancePage";
import LaborerBookingPage from "@/pages/LaborerBookingPage";
import B2BPage from "@/pages/B2BPage";
import AdminLoginPage from "@/pages/AdminLoginPage";
import AdminDashboardPage from "@/pages/AdminDashboardPage";
import AdminBookingsPage from "@/pages/AdminBookingsPage";
import InsurancePage from "@/pages/InsurancePage";
import PricingPage from "@/pages/PricingPage";
import PrivacyPage from "@/pages/PrivacyPage";
import TermsPage from "@/pages/TermsPage";
import PaymentSuccessPage from "@/pages/PaymentSuccessPage";
import PaymentCancelPage from "@/pages/PaymentCancelPage";
import CardAddedPage from "@/pages/CardAddedPage";
import CardCancelledPage from "@/pages/CardCancelledPage";
import SignupPage from "@/pages/SignupPage";
import LoginPage from "@/pages/LoginPage";
import AuthCallbackPage from "@/pages/AuthCallbackPage";
import ProfilePage from "@/pages/ProfilePage";
import PaymentMethodsPage from "@/pages/PaymentMethodsPage";
import PrivateChefPage from "@/pages/PrivateChefPage";
import NannyBookingPage from "@/pages/NannyBookingPage";
import CleaningBookingPage from "@/pages/CleaningBookingPage";
import MyBookingsPage from "@/pages/MyBookingsPage";
import ProfessionalVerificationPage from "@/pages/ProfessionalVerificationPage";
import ProfessionalDashboardPage from "@/pages/ProfessionalDashboardPage";
import ChatPage from "@/pages/ChatPage";
import WalletPage from "@/pages/WalletPage";
import LiveTrackingPage from "@/pages/LiveTrackingPage";
import TaxPage from "@/pages/TaxPage";
import ProfessionalDocumentsPage from "@/pages/ProfessionalDocumentsPage";
import AppSettingsPage from "@/pages/AppSettingsPage";
import ClientSettingsPage from "@/pages/ClientSettingsPage";
import SupportPage from "@/pages/SupportPage";
import PlusCardPage from "@/pages/PlusCardPage";
import AccountManagementPage from "@/pages/AccountManagementPage";
import AddressManagementPage from "@/pages/AddressManagementPage";
import DemandHeatmapPage from "@/pages/DemandHeatmapPage";
import EarningInsightsPage from "@/pages/EarningInsightsPage";
import ProSupportPage from "@/pages/ProSupportPage";
import InboxPage from "@/pages/InboxPage";
import MatchingPage from "@/pages/MatchingPage";
import SnowPlowingPage from "@/pages/SnowPlowingPage";
import LawnMowingPage from "@/pages/LawnMowingPage";
import ProActivityPage from "@/pages/ProActivityPage";
import ProMenuPage from "@/pages/ProMenuPage";
import ProBottomNav from "@/components/layout/ProBottomNav";

// Router component that handles auth callback detection
function AppRouter() {
  const location = useLocation();
  
  // Check URL fragment for session_id (Google OAuth callback)
  if (location.hash?.includes('session_id=')) {
    return <AuthCallbackPage />;
  }

  return (
    <>
    <Routes>
      {/* Public routes - No login required */}
      <Route path="/signup" element={<SignupPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/auth/callback" element={<AuthCallbackPage />} />
      <Route path="/admin/login" element={<AdminLoginPage />} />
      
      {/* Public routes - No login required */}
      <Route path="/" element={<HomePage />} />
      <Route path="/business" element={<B2BPage />} />
      <Route path="/cargo" element={<CargoBookingPage />} />
      <Route path="/courier" element={<CourierBookingPage />} />
      <Route path="/towing" element={<TowingBookingPage />} />
      <Route path="/movers" element={<MoversBookingPage />} />
      <Route path="/roadside-assistance" element={<RoadsideAssistancePage />} />
      <Route path="/laborer" element={<LaborerBookingPage />} />
      <Route path="/snow-plowing" element={<SnowPlowingPage />} />
      <Route path="/lawn-mowing" element={<LawnMowingPage />} />
      <Route path="/nanny" element={<NannyBookingPage />} />
      <Route path="/cleaning" element={<CleaningBookingPage />} />
      <Route path="/private-chef" element={<PrivateChefPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />

      {/* Protected routes - Login required */}
      <Route path="/profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
      <Route path="/my-bookings" element={<ProtectedRoute><MyBookingsPage /></ProtectedRoute>} />
      <Route path="/professional/verify" element={<ProtectedRoute><ProfessionalVerificationPage /></ProtectedRoute>} />
      <Route path="/professional/dashboard" element={<ProtectedRoute><ProfessionalDashboardPage /></ProtectedRoute>} />
      <Route path="/professional/activity" element={<ProtectedRoute><ProActivityPage /></ProtectedRoute>} />
      <Route path="/professional/menu" element={<ProtectedRoute><ProMenuPage /></ProtectedRoute>} />
      <Route path="/chat/:requestId" element={<ProtectedRoute><ChatPage /></ProtectedRoute>} />
      <Route path="/matching" element={<ProtectedRoute><MatchingPage /></ProtectedRoute>} />
      <Route path="/tracking/:requestId" element={<ProtectedRoute><LiveTrackingPage /></ProtectedRoute>} />
      <Route path="/wallet" element={<ProtectedRoute><WalletPage /></ProtectedRoute>} />
      <Route path="/tax" element={<ProtectedRoute><TaxPage /></ProtectedRoute>} />
      <Route path="/professional/documents" element={<ProtectedRoute><ProfessionalDocumentsPage /></ProtectedRoute>} />
      <Route path="/settings" element={<ProtectedRoute><AppSettingsPage /></ProtectedRoute>} />
      <Route path="/client/settings" element={<ProtectedRoute><ClientSettingsPage /></ProtectedRoute>} />
      <Route path="/support" element={<ProtectedRoute><SupportPage /></ProtectedRoute>} />
      <Route path="/plus-card" element={<ProtectedRoute><PlusCardPage /></ProtectedRoute>} />
      <Route path="/account" element={<ProtectedRoute><AccountManagementPage /></ProtectedRoute>} />
      <Route path="/address" element={<ProtectedRoute><AddressManagementPage /></ProtectedRoute>} />
      <Route path="/insurance" element={<ProtectedRoute><InsurancePage /></ProtectedRoute>} />
      <Route path="/professional/heatmap" element={<ProtectedRoute><DemandHeatmapPage /></ProtectedRoute>} />
      <Route path="/professional/earnings-insights" element={<ProtectedRoute><EarningInsightsPage /></ProtectedRoute>} />
      <Route path="/professional/support" element={<ProtectedRoute><ProSupportPage /></ProtectedRoute>} />
      <Route path="/inbox" element={<ProtectedRoute><InboxPage /></ProtectedRoute>} />
      <Route path="/payment-methods" element={<ProtectedRoute><PaymentMethodsPage /></ProtectedRoute>} />
      {/* Admin dashboard has its own auth check - don't wrap in user ProtectedRoute */}
      <Route path="/admin/dashboard" element={<AdminDashboardPage />} />
      <Route path="/admin/bookings" element={<AdminBookingsPage />} />
      <Route path="/pricing" element={<ProtectedRoute><PricingPage /></ProtectedRoute>} />
      <Route path="/terms" element={<ProtectedRoute><TermsPage /></ProtectedRoute>} />
      <Route path="/payment/success" element={<ProtectedRoute><PaymentSuccessPage /></ProtectedRoute>} />
      <Route path="/payment/cancel" element={<ProtectedRoute><PaymentCancelPage /></ProtectedRoute>} />
      <Route path="/payment/card-added" element={<ProtectedRoute><CardAddedPage /></ProtectedRoute>} />
      <Route path="/payment/card-cancelled" element={<ProtectedRoute><CardCancelledPage /></ProtectedRoute>} />
    </Routes>
    <ProBottomNav />
    </>
  );
}

function PushNotificationManager() {
  const { isAuthenticated } = useAuth();
  usePushNotifications(isAuthenticated);
  return null;
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <PushNotificationManager />
          <ScrollToTop />
          <AnalyticsTracker />
          <AppRouter />
        </BrowserRouter>
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </div>
  );
}

export default App;
